/**
 * @component ry-popover
 * @category  overlay
 * @summary   Anchored floating panel — the base primitive every overlay composes from. Renders authored content beside a trigger element (or at a virtual point), with auto-flip, shift, and optional arrow. Modal mode adds backdrop + focus trap + scroll lock. Hover and click triggers supported declaratively; manual mode exposes open/close for full programmatic control.
 *
 * Reference: combines the semantics of Fomantic UI's popup with the native
 * Popover API. ry-popover is the engine room for `<ry-tooltip>`,
 * `<ry-dropdown>`, `<ry-menu>`, `<ry-select>` panel, `<ry-combobox>` panel,
 * `<ry-hover-card>`, `<ry-context-menu>`, and `<ry-date-picker>` — they all
 * compose from this primitive. Use it directly when none of those fit.
 *
 * @attribute placement      'top' | 'top-start' | 'top-end' | 'bottom' | 'bottom-start' | 'bottom-end' | 'left' | 'left-start' | 'left-end' | 'right' | 'right-start' | 'right-end'   default="bottom-start"   Initial placement. Auto-flips if no room.
 * @attribute offset         number   default=8     Pixels of gap between trigger and floating element.
 * @attribute flip           boolean  default=true  Auto-flip to opposite side near viewport edge.
 * @attribute shift          boolean  default=true  Slide along main axis to stay in viewport.
 * @attribute arrow          boolean  default=false Render an arrow indicator pointing at the trigger. Arrow element auto-created on first open.
 * @attribute open           boolean  default=false Reflects state. Setting opens; removing closes. Prefer .open()/.close() for programmatic control.
 * @attribute trigger-event  'click' | 'hover' | 'focus' | 'manual' | 'context-menu'   default="click"   How the popover opens. `manual` = consumer drives via .open()/.close(). `context-menu` = right-click on trigger, anchored at cursor via virtual element.
 * @attribute hover-delay    number   default=100   Milliseconds before hover opens. Cancelled by mouseleave before delay completes.
 * @attribute match-width    boolean  default=false Match floating element's width to trigger's width. Used by select/combobox panels.
 * @attribute persistent     boolean  default=false Don't close on outside click or Escape. `.close()` and explicit triggers still work.
 * @attribute trap-focus     boolean  default=false Trap Tab focus within the popover while open.
 * @attribute lock-scroll    boolean  default=false Lock body scroll while open. Reference-counted with other overlays.
 * @attribute return-focus   boolean  default=true  When closing, restore focus to the element that was focused on open.
 * @attribute modal          boolean  default=false Convenience: enables trap-focus + lock-scroll + return-focus + adds backdrop. Use for dialog-style popovers.
 *
 * @property  state          'open' | 'closed'   Mirrors `data-ry-state`. Read-only outside open()/close().
 *
 * @method    open()         Show the popover. Idempotent — no-op + no event if already open.
 * @method    close()        Hide the popover. Idempotent.
 * @method    toggle()       Open if closed, close if open.
 * @method    setVirtualAnchor(x, y)   When trigger-event is `context-menu`, update the anchor position. Use for autocomplete / drag bubbles / cursor follow.
 *
 * @event ry:beforeopen    detail: {}                 fires at the top of open() — trigger click/hover/focus/context-menu or el.open() — before any state change. NOT fired for the silent `[open]`-at-mount path.   cancelable: preventDefault() keeps it closed; ry:open does not fire.
 * @event ry:open    detail: {}                       fires after state transitions to 'open' (synchronously after the state attribute is set; FUI repositioning happens immediately after).
 * @event ry:beforeclose   detail: {}                 fires at the top of close() — Escape, outside click, backdrop, [close], trigger toggle, or el.close() — before any state change.   cancelable: preventDefault() keeps it open; ry:close does not fire.
 * @event ry:close   detail: {}                       fires after state transitions to 'closed'.

 * @event ry:position detail: { x, y, placement }     fires after every reposition (rate-limited via rAF in autoUpdate). Use sparingly — most consumers don't need it.
 *
 * @keyboard  Escape — closes (unless `[persistent]`).
 *            Tab/Shift+Tab — cycle focus within popover when `[trap-focus]` is set.
 *
 * @state     `data-ry-state` of `"open"` or `"closed"`. CSS uses @starting-style + transition-behavior: allow-discrete for enter/exit animations.
 *
 * @content   Free-form. Whatever you author inside `<ry-popover>` becomes the panel body. A descendant with `[close]` attribute auto-wires to call `.close()` on click.
 *
 * @dom
 *   ry-popover[id][data-ry-state="open"|"closed"][modal?]
 *     > div.ry-popover__floating  [data-ry-target="floating"]  (portals to <body> on open)
 *       > div.ry-popover__arrow   [data-ry-target="arrow"]     (only when [arrow])
 *       > {authored content}
 *   ry-popover[modal]
 *     > div.ry-popover__backdrop  [data-ry-target="backdrop"]  (only when [modal], portals with floating)
 *
 * @gotcha  Triggers are paired by ID: `<button popover-target="my-id">` opens `<ry-popover id="my-id">`. NOTE: the attribute is `popover-target=` NOT `popover=` — the bare `popover=` attribute is part of the native HTML5 Popover API and gives the element UA-level `position: fixed; top: 0; inset: 0` styling, which conflicts with our custom positioning. Triggers are scanned at the popover's setup time. Adding triggers AFTER mount does NOT auto-wire — call `el.open()` directly.
 *
 * @gotcha  The floating element portals to `<body>` on open and returns to its authored DOM position on close. Don't rely on `ry-popover.contains(el)` mid-open — the panel is technically a child of `<body>` then.
 *
 * @gotcha  `[trigger-event="hover"]` does NOT fire `ry:open` for keyboard focus. If you want both, use `[trigger-event="focus"]` (which covers hover too via CSS `:hover` if needed) OR drive opens programmatically.
 *
 * @gotcha  `[persistent]` blocks `Escape` AND outside-click. Explicit `.close()` calls, `[close]` attribute buttons, and trigger-click (when `trigger-event="click"`) still work.
 *
 * @gotcha  Setting `[open]` attribute opens the popover only at setup time. After mount, the attribute reflects state but doesn't drive it — use `.open()` / `.close()`.
 *
 * @example basic
 * ```html
 * <ry-button popover-target="info">?</ry-button>
 * <ry-popover id="info">
 *   Some helpful context.
 * </ry-popover>
 * ```
 *
 * @example placement
 * ```html
 * <ry-button popover-target="p1">Bottom (default)</ry-button>
 * <ry-popover id="p1">Bottom</ry-popover>
 *
 * <ry-button popover-target="p2">Top</ry-button>
 * <ry-popover id="p2" placement="top">Top</ry-popover>
 *
 * <ry-button popover-target="p3">Right</ry-button>
 * <ry-popover id="p3" placement="right">Right</ry-popover>
 * ```
 *
 * @example with-arrow
 * ```html
 * <ry-button popover-target="tip">Hover for tip</ry-button>
 * <ry-popover id="tip" arrow trigger-event="hover" placement="top">
 *   Hello!
 * </ry-popover>
 * ```
 *
 * @example modal-dialog
 * ```html
 * <ry-button popover-target="confirm">Delete</ry-button>
 * <ry-popover id="confirm" modal>
 *   <h2>Are you sure?</h2>
 *   <ry-button close>Cancel</ry-button>
 *   <ry-button variant="danger" close>Delete</ry-button>
 * </ry-popover>
 * ```
 *
 * @tutorial mount-renders-floating-element
 * ```js
 * const el = mount('<ry-popover id="x">Hi</ry-popover>');
 * await expectEqual(!!el.querySelector('[data-ry-target="floating"]'), true);
 * ```
 *
 * @tutorial closed-by-default
 * ```js
 * const el = mount('<ry-popover id="x">Hi</ry-popover>');
 * await expectEqual(el.state, 'closed');
 * ```
 *
 * @tutorial open-fires-ry-open-once
 * ```js
 * const el = mount('<ry-popover id="x">Hi</ry-popover>');
 * const fired = [];
 * el.addEventListener('ry:open', () => fired.push(1));
 * el.open();
 * el.open();
 * el.open();
 * await expectEqual(fired.length, 1, 'idempotent');
 * ```
 *
 * @tutorial close-restores-portal
 * ```js
 * // Wrong assumption: the floating element stays a child of ry-popover.
 * // On open it portals to <body>, on close it returns to its authored parent.
 * const el = mount('<ry-popover id="x">Hi</ry-popover>');
 * const floating = el.querySelector('[data-ry-target="floating"]');
 * el.open();
 * await expectEqual(floating.parentElement, document.body, 'portalled on open');
 * el.close();
 * await expectEqual(floating.parentElement, el, 'restored on close');
 * ```
 *
 * @tutorial close-attribute-wires-up
 * ```js
 * const el = mount(`
 *   <ry-popover id="x">
 *     <button close>OK</button>
 *   </ry-popover>
 * `);
 * el.open();
 * el.querySelector('button[close]').click();
 * await expectEqual(el.state, 'closed');
 * ```
 *
 * @tutorial escape-closes-unless-persistent
 * ```js
 * const el = mount('<ry-popover id="x">Hi</ry-popover>');
 * el.open();
 * document.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape' }));
 * await expectEqual(el.state, 'closed');
 *
 * const persistent = mount('<ry-popover id="y" persistent>Hi</ry-popover>');
 * persistent.open();
 * document.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape' }));
 * await expectEqual(persistent.state, 'open', 'persistent ignores escape');
 * ```
 *
 * @tutorial outside-click-closes
 * ```js
 * const el = mount('<ry-popover id="x">Hi</ry-popover>');
 * el.open();
 * document.body.click();
 * await expectEqual(el.state, 'closed');
 * ```
 *
 * @tutorial modal-implies-trap-and-lock
 * ```js
 * const el = mount('<ry-popover id="x" modal>Hi</ry-popover>');
 * el.open();
 * await expectAttr(document.body, 'data-ry-scroll-lock', '');
 * el.close();
 * ```
 *
 * @tutorial beforeopen-veto
 * ```js
 * // preventDefault() on ry:beforeopen keeps the popover closed; ry:open is
 * // silent. Without the veto, open() works.
 * const el = mount('<ry-popover id="x">Hi</ry-popover>');
 * const opened = [];
 * el.addEventListener('ry:open', () => opened.push(1));
 * el.addEventListener('ry:beforeopen', (e) => e.preventDefault(), { once: true });
 * el.open();
 * await expectEqual(el.state, 'closed', 'vetoed open leaves state closed');
 * await expectEqual(opened.length, 0, 'ry:open did not fire');
 * el.open();
 * await expectEqual(el.state, 'open', 'without the veto, open() works');
 * ```
 *
 * @tutorial beforeclose-veto
 * ```js
 * // preventDefault() on ry:beforeclose keeps the popover open — Escape,
 * // outside click, [close], or el.close(). ry:close is silent.
 * const el = mount('<ry-popover id="x">Hi</ry-popover>');
 * el.open();
 * const closed = [];
 * el.addEventListener('ry:close', () => closed.push(1));
 * el.addEventListener('ry:beforeclose', (e) => e.preventDefault(), { once: true });
 * el.close();
 * await expectEqual(el.state, 'open', 'vetoed close leaves state open');
 * await expectEqual(closed.length, 0, 'ry:close did not fire');
 * el.close();
 * await expectEqual(el.state, 'closed', 'without the veto, close() works');
 * ```
 *
 * @chain ry-button
 * ```js
 * // <ry-button popover-target="id"> is the canonical declarative trigger.

 * const root = mount(`
 *   <div>
 *     <ry-button popover-target="demo">Open</ry-button>
 *     <ry-popover id="demo">Hi</ry-popover>
 *   </div>
 * `);
 * const trigger = root.querySelector('ry-button');
 * const pop = root.querySelector('ry-popover');
 * trigger.click();
 * await expectEqual(pop.state, 'open');
 * ```
 *
 * @see ry-tooltip
 * @see ry-dropdown
 * @see ry-menu
 * @see ry-modal
 */

import { RyElement } from '../core/ry-element.js';
import { attachFloating, createVirtualAnchor, type Placement } from '../core/ry-floating.js';

type TriggerEvent = 'click' | 'hover' | 'focus' | 'manual' | 'context-menu';

declare global {
  interface HTMLElementEventMap {
    'ry:position': CustomEvent<{ x: number; y: number; placement: Placement }>;
  }
}

export class RyPopover extends RyElement {
  #floating: HTMLElement | null = null;
  #arrow: HTMLElement | null = null;
  #backdrop: HTMLElement | null = null;
  #floatingRestore: (() => void) | null = null;
  #backdropRestore: (() => void) | null = null;
  #attachCleanup: (() => void) | null = null;
  #outsideClickCleanup: (() => void) | null = null;
  #escapeCleanup: (() => void) | null = null;
  #restoreFocus: (() => void) | null = null;
  #hoverTimeout: number | null = null;
  #virtualAnchor: ReturnType<typeof createVirtualAnchor> | null = null;
  #triggers: Set<HTMLElement> = new Set();
  #initialContent: ChildNode[] = [];

  setup(): void {
    // Capture authored content before we restructure
    const authored = Array.from(this.childNodes);

    // Build floating wrapper
    const floating = document.createElement('div');
    floating.setAttribute('data-ry-target', 'floating');
    floating.className = 'ry-popover__floating';
    floating.style.position = 'absolute';
    floating.style.top = '0';
    floating.style.left = '0';

    // Optional arrow
    if (this.hasAttribute('arrow')) {
      const arrow = document.createElement('div');
      arrow.setAttribute('data-ry-target', 'arrow');
      arrow.className = 'ry-popover__arrow';
      arrow.style.position = 'absolute';
      floating.appendChild(arrow);
      this.#arrow = arrow;
    }

    // Move authored content into floating wrapper
    for (const node of authored) floating.appendChild(node);
    this.#initialContent = Array.from(floating.childNodes);

    // Clear the host and append the wrapper
    this.innerHTML = '';
    this.appendChild(floating);
    this.#floating = floating;

    // Backdrop for modal mode
    if (this.hasAttribute('modal')) {
      const backdrop = document.createElement('div');
      backdrop.setAttribute('data-ry-target', 'backdrop');
      backdrop.className = 'ry-popover__backdrop';
      this.appendChild(backdrop);
      this.#backdrop = backdrop;

      // Backdrop click closes (unless persistent)
      this.on(backdrop, 'click', () => {
        if (!this.hasAttribute('persistent')) this.close();
      });
    }

    // [close] attribute on any descendant
    this.on(floating, 'click', (e: Event) => {
      const t = e.target as Element | null;
      const closeBtn = t?.closest('[close]');
      if (closeBtn) this.close();
    });

    // ARIA
    if (!this.hasAttribute('role')) {
      this.setAttribute('role', this.hasAttribute('modal') ? 'dialog' : 'tooltip');
    }
    if (this.hasAttribute('modal') && !this.hasAttribute('aria-modal')) {
      this.setAttribute('aria-modal', 'true');
    }

    // Initial state
    if (!this.hasAttribute('data-ry-state')) {
      this.state = this.hasAttribute('open') ? 'open' : 'closed';
    }

    // Wire up triggers
    this.#wireTriggers();

    // Global listeners
    this.#outsideClickCleanup = this.onOutsideClick(() => this.close(), {
      skip: () => this.state !== 'open' || this.hasAttribute('persistent'),
      ignore: this.#triggers,
    });
    this.#escapeCleanup = this.onEscape(() => this.close(), {
      skip: () => this.state !== 'open' || this.hasAttribute('persistent'),
    });

    // If initially open, run the open machinery to portal + position
    if (this.state === 'open') {
      // Defer so the rest of the page has a chance to mount first
      queueMicrotask(() => this.#applyOpen(/* silent */ true));
    }
  }

  teardown(): void {
    this.#detach();
    if (this.#hoverTimeout != null) {
      clearTimeout(this.#hoverTimeout);
      this.#hoverTimeout = null;
    }
  }

  // ── Public API ──────────────────────────────────────────────────

  open(): void {
    if (this.state === 'open') return;
    if (!this.emit('beforeopen')) return;
    this.#applyOpen(false);
  }

  close(): void {
    if (this.state === 'closed') return;
    if (!this.emit('beforeclose')) return;
    this.state = 'closed';


    // Mirror state to closed on floating + backdrop
    if (this.#floating) this.#floating.setAttribute('data-ry-state', 'closed');
    if (this.#backdrop) this.#backdrop.setAttribute('data-ry-state', 'closed');

    // Detach floating logic
    this.#detach();

    // Restore body scroll lock + focus
    if (this.#shouldLockScroll()) this.unlockScroll();
    this.#restoreFocus?.();
    this.#restoreFocus = null;

    // Remove backdrop attribute hook from body
    if (this.hasAttribute('modal')) {
      document.body.removeAttribute('data-ry-popover-modal');
    }

    this.emit('close');
  }

  toggle(): void {
    if (this.state === 'open') this.close(); else this.open();
  }

  setVirtualAnchor(x: number, y: number): void {
    if (!this.#virtualAnchor) {
      this.#virtualAnchor = createVirtualAnchor({ x, y });
    } else {
      this.#virtualAnchor.update({ x, y });
    }
  }

  // ── Internals ───────────────────────────────────────────────────

  #applyOpen(silent: boolean): void {
    if (!this.#floating) return;
    this.state = 'open';

    // Mirror state onto the floating element + backdrop. CSS keys on these
    // attributes (NOT on the host's data-ry-state via descendant selector)
    // so visibility/transition work after portal.
    this.#floating.setAttribute('data-ry-state', 'open');
    if (this.#backdrop) this.#backdrop.setAttribute('data-ry-state', 'open');

    // Focus capture for return-focus
    if (this.hasAttribute('modal') || this.getAttribute('return-focus') !== 'false') {
      this.#restoreFocus = this.withReturnFocus();
    }

    // Scroll lock if needed
    if (this.#shouldLockScroll()) this.lockScroll();

    // Modal hook for backdrop CSS
    if (this.hasAttribute('modal')) {
      document.body.setAttribute('data-ry-popover-modal', '');
      this.#floating.classList.add('ry-popover__floating--modal');
    }

    // Portal floating + backdrop to <body>
    this.#floatingRestore = this.portalToBody(this.#floating);
    if (this.#backdrop) {
      this.#backdropRestore = this.portalToBody(this.#backdrop);
    }

    // Choose reference: virtual anchor (context-menu / programmatic) or first trigger
    const ref = this.#virtualAnchor
      ? this.#virtualAnchor.element
      : this.#firstTrigger() ?? this;

    // Attach floating positioning
    this.#attachCleanup = attachFloating(ref, this.#floating, {
      placement: this.#placement(),
      offset: this.#offset(),
      flip: this.getAttribute('flip') !== 'false',
      shift: this.getAttribute('shift') !== 'false',
      matchWidth: this.hasAttribute('match-width'),
      arrow: this.#arrow,
      onPosition: ({ x, y, placement }) => {
        this.emit('position', { x, y, placement });
      },
    });

    // Focus trap
    if (this.#shouldTrapFocus()) {
      this.trapFocus(this.#floating);
    }

    if (!silent) this.emit('open');
  }

  #detach(): void {
    this.#attachCleanup?.();
    this.#attachCleanup = null;
    this.#floatingRestore?.();
    this.#floatingRestore = null;
    this.#backdropRestore?.();
    this.#backdropRestore = null;
  }

  #wireTriggers(): void {
    const id = this.id;
    if (!id) return;
    const triggers = document.querySelectorAll<HTMLElement>(`[popover-target="${id}"]`);
    triggers.forEach(t => this.#triggers.add(t));

    const triggerEvent = (this.getAttribute('trigger-event') ?? 'click') as TriggerEvent;

    triggers.forEach(trigger => {
      if (triggerEvent === 'click') {
        this.on(trigger, 'click', (e: Event) => {
          e.stopPropagation();
          this.toggle();
        });
      } else if (triggerEvent === 'hover') {
        this.on(trigger, 'mouseenter', () => this.#hoverOpen());
        this.on(trigger, 'mouseleave', () => this.#hoverClose());
        this.on(trigger, 'focusin', () => this.#hoverOpen());
        this.on(trigger, 'focusout', () => this.#hoverClose());
      } else if (triggerEvent === 'focus') {
        this.on(trigger, 'focusin', () => this.open());
        this.on(trigger, 'focusout', () => this.close());
      } else if (triggerEvent === 'context-menu') {
        this.on(trigger, 'contextmenu', (e: Event) => {
          const me = e as MouseEvent;
          me.preventDefault();
          this.setVirtualAnchor(me.clientX, me.clientY);
          this.open();
        });
      }
      // 'manual' = no listeners; consumer drives
    });
  }

  #hoverOpen(): void {
    if (this.#hoverTimeout != null) {
      clearTimeout(this.#hoverTimeout);
      this.#hoverTimeout = null;
    }
    const delay = Number(this.getAttribute('hover-delay') ?? 100);
    this.#hoverTimeout = window.setTimeout(() => {
      this.#hoverTimeout = null;
      this.open();
    }, delay);
  }

  #hoverClose(): void {
    if (this.#hoverTimeout != null) {
      clearTimeout(this.#hoverTimeout);
      this.#hoverTimeout = null;
    }
    this.close();
  }

  #firstTrigger(): HTMLElement | null {
    return this.#triggers.size > 0 ? this.#triggers.values().next().value ?? null : null;
  }

  #placement(): Placement {
    return (this.getAttribute('placement') ?? 'bottom-start') as Placement;
  }

  #offset(): number {
    const v = this.getAttribute('offset');
    return v == null ? 8 : Number(v);
  }

  #shouldTrapFocus(): boolean {
    return this.hasAttribute('trap-focus') || this.hasAttribute('modal');
  }

  #shouldLockScroll(): boolean {
    return this.hasAttribute('lock-scroll') || this.hasAttribute('modal');
  }
}

customElements.define('ry-popover', RyPopover);
